import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/Header';
import HelpBenefits, { HELP_URLS } from '@/components/HelpBenefits';
import AdminHiddenSectionsOverview from '@/components/AdminHiddenSectionsOverview';
import ModuleExamplesBar from '@/components/ModuleExamplesBar';
import { useCuratedExamples } from '@/hooks/useCuratedExamples';
import UpdateCuratedExampleButton from '@/components/UpdateCuratedExampleButton';
import { useSortFilterBar } from '@/components/SortFilterBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Calculator, Save, Trash2, ChevronDown, ChevronUp, Clock, FolderOpen, Pencil, Check, X as XIcon, Lock } from 'lucide-react';
import { InlineRename } from '@/components/InlineRename';
import { useAuth } from '@/App';
import { useCredits } from '@/contexts/CreditsContext';
import WingmanInsight from '@/components/WingmanInsight';
import WingmanNudge, { detectSFRPatterns } from '@/components/WingmanNudge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_SPECIES = [
  { value: 'Seabream', label: 'Seabream (Sparus aurata)' },
  { value: 'Seabass', label: 'Seabass (Dicentrarchus labrax)' },
  { value: 'Tilapia', label: 'Tilapia (Oreochromis niloticus)' },
];
const COMING_SOON_SPECIES = [
  { value: 'Salmo salar', label: 'Atlantic Salmon' },
];

// Per-species reference info shown in the form + result card. Adding a new
// species here is enough — no other places in this file need changing.
const SPECIES_META = {
  Seabream: {
    common: 'Seabream',
    scientific: 'Sparus aurata',
    tempRange: '14-30°C',
  },
  Seabass: {
    common: 'Seabass',
    scientific: 'Dicentrarchus labrax',
    tempRange: '12-28°C',
  },
  Tilapia: {
    common: 'Tilapia',
    scientific: 'Oreochromis niloticus',
    tempRange: '16-34°C',
  },
};

// Backward-compat: map any legacy scientific species value (used in older
// saved records) back to the canonical common-name key in SPECIES_META.
const SPECIES_ALIAS = {
  'Sparus aurata': 'Seabream',
  'Dicentrarchus labrax': 'Seabass',
  'Oreochromis niloticus': 'Tilapia',
};

const resolveSpeciesMeta = (raw) =>
  SPECIES_META[raw] || SPECIES_META[SPECIES_ALIAS[raw]] || null;

export default function SFRCalculator() {
  const { user, token } = useAuth();
  const { formatCostLabel } = useCredits();
  // Read-only mode: user not approved (pending admin approval)
  const isReadOnly = !user?.is_approved && !user?.is_unlimited && !user?.is_admin;
  const [formData, setFormData] = useState({
    species: 'Seabream',
    temperature_celsius: '',
    fish_count: '',
    average_weight_g: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Save/Load state
  const [savedRecords, setSavedRecords] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [activeRecordId, setActiveRecordId] = useState(null);

  // Excel-style sort + multi-select filter over saved SFR calculations.
  const sfrRecordColumns = useMemo(() => ([
    { key: 'name',         label: 'Name',          accessor: r => r.name,                          type: 'text' },
    { key: 'created_at',   label: 'Created',       accessor: r => r.created_at,                    type: 'date' },
    { key: 'sfr_pct',      label: 'SFR %',         accessor: r => r.result?.sfr_percentage,        type: 'number' },
    { key: 'feed_kg',      label: 'Feed (kg/day)', accessor: r => r.result?.total_feed_kg,         type: 'number' },
    { key: 'species',      label: 'Species',       accessor: r => r.input?.species || r.species,   type: 'text' },
  ]), []);
  const { processedRecords: filteredSfrRecords, bar: sfrSortFilterBar } = useSortFilterBar(
    savedRecords,
    sfrRecordColumns,
    'sfr-records',
  );

  // Admin-curated "Try [Species]" presets (replaces the old hardcoded
  // SFR_EXAMPLES — admin chooses the source record per species in the
  // AdminConsole > Curated Examples panel).
  const { examples: sfrExamples, refetch: refetchSfrExamples } = useCuratedExamples('sfr');

  const isDemo = user?.is_demo === true;
  const formRef = useRef(null);
  // Set to true by `loadExampleAndRun` and consumed inside `handleSubmit`
  // so curated "Try [Species]" runs POST with `?example=1` (quota-free).
  const exampleRunRef = useRef(false);
  // Tracks the curated example just loaded so the admin "Update Try-X" CTA
  // can render. Cleared whenever a different (non-curated) record loads.
  const [loadedCurated, setLoadedCurated] = useState(null);

  const loadExampleAndRun = (preset) => {
    if (isReadOnly) return;
    setFormData((prev) => ({ ...prev, ...preset.inputs }));
    toast.success(`Loaded ${preset.species_label} example — running calculation…`);
    // Flag this submit as a curated example run so the POST goes out with
    // `?example=1` and the backend skips credit billing.
    exampleRunRef.current = true;
    if (preset?.source_record_id) {
      setActiveRecordId(preset.source_record_id);
      setLoadedCurated({
        module: 'sfr',
        species: preset.species_label,
        source_record_id: preset.source_record_id,
        current_name: preset.source_record_name || `Try-${preset.species_label} Example`,
      });
    }
    setTimeout(() => { formRef.current?.requestSubmit?.(); }, 50);
  };

  const fetchRecords = async () => {
    try {
      const authToken = token; // from useAuth
      const res = await axios.get(`${BACKEND_URL}/api/sfr-records`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedRecords(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchRecords(); }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const authToken = token; // from useAuth
    try {
      const isExampleRun = exampleRunRef.current;
      exampleRunRef.current = false;

      const response = await axios.post(
        `${BACKEND_URL}/api/calculator/sfr${isExampleRun ? '?example=1' : ''}`,
        {
          species: formData.species,
          temperature_celsius: parseFloat(formData.temperature_celsius),
          fish_count: parseInt(formData.fish_count),
          average_weight_g: parseFloat(formData.average_weight_g),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      setActiveRecordId(null);
      toast.success('SFR calculated successfully!');
    } catch (error) {
      // FastAPI returns `detail` as either a string (HTTP 400 raise) or a
      // list of validation objects (HTTP 422). Passing the array directly to
      // toast.error renders {} children and crashes the React tree, so coerce
      // here. The shared helper `formatApiErrorDetail` (utils/errorMessage)
      // is intentionally NOT used to keep this page self-contained.
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d?.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : 'Calculation failed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!result) { toast.error('No result to save'); return; }
    const name = saveName.trim() || `SFR ${formData.species} ${formData.temperature_celsius}°C ${formData.average_weight_g}g`;
    setSaving(true);
    try {
      const authToken = token; // from useAuth
      await axios.post(`${BACKEND_URL}/api/sfr-records`, {
        name,
        species: formData.species,
        form_data: formData,
        result,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('SFR record saved');
      setSaveName('');
      fetchRecords();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d?.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : 'Failed to save');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const loadRecord = (record) => {
    if (record.form_data) {
      setFormData(record.form_data);
    }
    if (record.result) {
      setResult(record.result);
    }
    setActiveRecordId(record.id);
    setLoadedCurated(null);
    toast.success(`Loaded: ${record.name}`);
  };

  const confirmDelete = (id) => {
    setPendingDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      const authToken = token; // from useAuth
      await axios.delete(`${BACKEND_URL}/api/sfr-records/${pendingDeleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Record deleted');
      if (activeRecordId === pendingDeleteId) setActiveRecordId(null);
      fetchRecords();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d?.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : 'Failed to delete');
      toast.error(msg);
    }
    setPendingDeleteId(null);
  };

  const renameSfrRecord = async (recordId, newName) => {
    const authToken = token; // from useAuth
    await axios.patch(
      `${BACKEND_URL}/api/sfr-records/${recordId}/rename`,
      { name: newName },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setSavedRecords(prev => prev.map(r => r.id === recordId ? { ...r, name: newName } : r));
    toast.success('Record renamed');
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <ModuleExamplesBar
          examples={sfrExamples}
          onSelect={loadExampleAndRun}
          moduleLabel="SFR Calculator"
          isReadOnly={isReadOnly}
        />
        <Card className="glass-card border-[#0891b2] shadow-lg">
          <CardHeader>
            <CardTitle className="text-[#0891b2] flex items-center gap-2">
              <Calculator className="w-6 h-6" />
              SFR Calculator
            </CardTitle>
            <CardDescription className="text-sm text-gray-600">
              Calculate recommended Specific Feed Rate based on species, temperature and fish weight
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Read-Only Mode Banner */}
            {isReadOnly && (
              <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="read-only-banner">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Read-Only Mode</p>
                    <p className="text-xs text-amber-700">Your account is pending approval. You can explore the interface but cannot run calculations.</p>
                  </div>
                </div>
              </div>
            )}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <fieldset disabled={isReadOnly} className={isReadOnly ? 'opacity-60' : ''}>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-700">Species</Label>
                  <Select value={formData.species} onValueChange={(v) => handleInputChange('species', v)} disabled={isReadOnly}>
                    <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectGroup>
                        <SelectLabel className="text-[#0891b2] font-semibold">Available Species</SelectLabel>
                        {SUPPORTED_SPECIES.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-gray-400 font-semibold">Coming Soon</SelectLabel>
                        {COMING_SOON_SPECIES.map(s => (<SelectItem key={s.value} value={s.value} disabled className="text-gray-400 italic">{s.label} (Coming soon)</SelectItem>))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="temperature" className="text-gray-700">Water Temperature (°C)</Label>
                  <Input id="temperature" type="number" step="0.1" placeholder="e.g., 22.5" value={formData.temperature_celsius} onChange={(e) => handleInputChange('temperature_celsius', e.target.value)} required className="bg-white border-gray-300 focus:border-[#0891b2]" />
                  <p className="text-xs text-gray-500" data-testid="sfr-temp-hint">
                    {(() => {
                      const meta = resolveSpeciesMeta(formData.species);
                      return meta ? `Valid range: ${meta.tempRange} (${meta.common})` : 'Select a species above to see the valid temperature range';
                    })()}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fish_count" className="text-gray-700">Fish Count</Label>
                  <Input id="fish_count" type="number" placeholder="e.g., 50000" value={formData.fish_count} onChange={(e) => handleInputChange('fish_count', e.target.value)} required className="bg-white border-gray-300 focus:border-[#0891b2]" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="average_weight" className="text-gray-700">Average Weight (grams)</Label>
                  <Input id="average_weight" type="number" step="0.1" min={(formData.species || '').toLowerCase().includes('tilapia') ? '20' : '0.1'} placeholder={(formData.species || '').toLowerCase().includes('tilapia') ? 'Min 20g' : 'e.g., 250.5'} value={formData.average_weight_g} onChange={(e) => handleInputChange('average_weight_g', e.target.value)} required className="bg-white border-gray-300 focus:border-[#0891b2]" />
                  {(formData.species || '').toLowerCase().includes('tilapia') && (
                    <p className="text-xs text-emerald-700" data-testid="tilapia-min-weight-note">
                      For Tilapia: minimum average weight is <strong>20 g</strong>.
                    </p>
                  )}
                </div>
              </div>
              <Button type="submit" disabled={loading || isReadOnly} className="w-full bg-[#0891b2] hover:bg-[#06748c] text-white">
                {isReadOnly ? <><Lock className="w-4 h-4 mr-2" />Pending Approval</> : loading ? 'Calculating...' : `Calculate SFR${formatCostLabel('sfr_calculator')}`}
              </Button>
              <div className="flex justify-center pt-2">
                <UpdateCuratedExampleButton
                  module="sfr"
                  species={loadedCurated?.species}
                  currentSpecies={formData.species}
                  token={token}
                  isAdmin={!!user?.is_admin}
                  sourceRecordId={loadedCurated?.source_record_id}
                  currentRecordId={activeRecordId}
                  currentName={loadedCurated?.current_name}
                  buildRecordPatch={() => ({
                    species: formData.species,
                    form_data: formData,
                  })}
                  onUpdated={() => refetchSfrExamples?.()}
                />
              </div>
              </fieldset>
            </form>
          </CardContent>
        </Card>

        {result && (
          <Card className="glass-card border-[#0891b2] shadow-lg">
            <CardHeader>
              <CardTitle className="text-[#0891b2]">Calculation Results</CardTitle>
              <CardDescription className="text-sm text-gray-600" data-testid="sfr-result-card-description">
                {(() => {
                  const meta = resolveSpeciesMeta(result.species);
                  return meta
                    ? `Based on ${meta.common} (${meta.scientific}) SFR reference tables`
                    : 'Based on internal SFR reference tables';
                })()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Species</p>
                  <p className="text-lg font-semibold text-gray-800">{result.species}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Temperature</p>
                  <p className="text-lg font-semibold text-gray-800">{result.temperature_celsius}°C</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Fish Count</p>
                  <p className="text-lg font-semibold text-gray-800">{result.fish_count?.toLocaleString()}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Average Weight</p>
                  <p className="text-lg font-semibold text-gray-800">{result.average_weight_g}g</p>
                </div>
              </div>
              <div className="bg-[#0891b2]/10 p-6 rounded-lg border-2 border-[#0891b2]">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Specific Feed Rate (SFR)</p>
                    <p className="text-2xl font-bold text-[#0891b2]">{result.sfr_percentage}%</p>
                  </div>
                  <div className="border-t border-[#0891b2]/30 pt-4">
                    <p className="text-sm text-gray-600 mb-1">Total Recommended Feed per Day</p>
                    <p className="text-2xl font-bold text-[#06748c]">{result.total_feed_kg} kg</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800" data-testid="sfr-result-note">
                  <strong>Note:</strong> This calculation is based on internal reference tables for{' '}
                  {(() => {
                    const meta = resolveSpeciesMeta(result.species);
                    return meta
                      ? `${meta.common} (${meta.scientific})`
                      : result.species;
                  })()}
                  . Actual feeding rates should be adjusted based on fish behavior, appetite, and farm conditions.
                </p>
              </div>

              {/* Save Section */}
              {!isDemo && (
                <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                  <Input
                    placeholder="Record name (optional)"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    className="flex-1 bg-white"
                    data-testid="sfr-save-name"
                  />
                  <Button onClick={handleSave} disabled={saving} className="bg-[#0891b2] hover:bg-[#06748c] text-white" data-testid="sfr-save-btn">
                    <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Wingman Proactive Alert */}
        {result && <WingmanNudge patterns={detectSFRPatterns({ result })} />}

        {/* Wingman AI Insight */}
        {result && (
          <WingmanInsight
            analysisType="SFR Calculator"
            analysisData={{
              species: result.species,
              temperature: result.temperature_celsius,
              fish_count: result.fish_count,
              average_weight: result.average_weight_g,
              sfr_percentage: result.sfr_percentage,
              total_feed_kg: result.total_feed_kg
            }}
          />
        )}

        {/* Saved Records Section */}
        <Collapsible open={showSaved} onOpenChange={setShowSaved}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-testid="sfr-saved-toggle">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-[#0891b2]" />
                <span className="font-medium text-gray-700 text-sm">Saved SFR Calculations ({savedRecords.length})</span>
              </div>
              {showSaved ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            {savedRecords.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-4">No saved calculations yet</p>
            ) : (
              <div className="space-y-2">
                {sfrSortFilterBar}
                {filteredSfrRecords.map(record => (
                  <div key={record.id} className={`flex items-center justify-between p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50 transition ${activeRecordId === record.id ? 'border-[#0891b2] bg-[#0891b2]/5' : 'border-gray-200'}`} data-testid={`sfr-record-${record.id}`}>
                    <div className="flex-1 min-w-0" onClick={() => loadRecord(record)}>
                      <div className="text-sm font-medium text-gray-800">
                        <InlineRename
                          value={record.name}
                          onSave={(newName) => renameSfrRecord(record.id, newName)}
                          disabled={isDemo}
                        />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(record.created_at).toLocaleDateString()}</span>
                        <span>|</span>
                        <span>{record.result?.sfr_percentage}% SFR</span>
                        <span>|</span>
                        <span>{record.result?.total_feed_kg} kg/day</span>
                      </div>
                    </div>
                    {!isDemo && (
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 ml-2" onClick={() => confirmDelete(record.id)} data-testid={`sfr-delete-${record.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Admin-only: Save / Discard report-visibility changes */}
        <AdminHiddenSectionsOverview className="mb-4" />

        <HelpBenefits youtubeUrl={HELP_URLS.sfrCalculator.youtube} benefitsUrl={HELP_URLS.sfrCalculator.benefits} featureName="SFR Calculator" />
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SFR Record</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this saved calculation? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
